import { queryOptions, RefetchOptions, useSuspenseQuery } from "@tanstack/react-query";
import { httpPostGraphql } from "shared/api/httpClient";
import { useUserId } from "shared/stores/oidc";
import { fail } from "shared/utils/fns";
import { graphql } from "src/gql";
import { GroupLikeFragment, UserQuery } from "src/gql/graphql";

graphql(`
    fragment EudLike on EUDNode {
        id
        invalidDate
        isDefault
        isVerified
        location
        name
        type
        value
        optOut {
            sms
            system
        }
    }
`);

export function useUser(opts?: { assert?: false }): { user?: UserQuery["user"]; refetch: (opts?: RefetchOptions) => Promise<unknown> };
export function useUser(opts: { assert: true }): { user: UserQuery["user"]; refetch: (opts?: RefetchOptions) => Promise<unknown> };

export function useUser(opts?: { id?: string; assert?: boolean }): unknown {
    const { userId, isSupport } = useUserId();
    const { data, refetch } = useSuspenseQuery({ ...userQueryOptions({ userId, isSupport }) });

    if (opts?.assert && isSupport) fail("User presence asserted, but found support user.");
    if (opts?.assert && data?.user == undefined) fail("User presence asserted, but user is undefined.");

    return { user: data?.user, refetch };
}

// TODO: implement UI for support users to view other users's groups
export function useUserGroups(id?: string) {
    const { userId, isSupport } = useUserId();
    if (isSupport && !id) return { groups: [] };
    const { data: groups } = useSuspenseQuery({
        ...userQueryOptions({ userId: id ?? userId, isSupport }),
        select: (data) => data?.user.groups ?? [],
    });
    return { groups: groups as GroupLikeFragment[] };
}

export function useUserGroupsWithChildren(id?: string) {
    const { userId, isSupport } = useUserId();
    if (isSupport && !id) return { groups: [] };
    const { data: rootGroups } = useSuspenseQuery({
        queryKey: ["user", "id", userId, "groupsWithChildren"],
        queryFn: async () => {
            if (isSupport) return [];
            const userData = await httpPostGraphql(queries.user, { id: userId });
            const rootGroupsData = await Promise.all(
                (userData?.user.groups ?? []).map(({ id }) => httpPostGraphql(queries.groupWithChildren, { id })),
            );
            return rootGroupsData.map((result) => result?.group).filter(Boolean);
        },
    });

    const allChildren = rootGroups.flatMap((group) => group?.childrenPaged ?? []);
    const allGroups = [...rootGroups, ...allChildren];
    // Deduplicate since child can appear under multiple parents
    const dedupedGroups = Array.from(new Map(allGroups.map((g) => [g.id, g])).values());
    return { groups: dedupedGroups as GroupLikeFragment[] };
}

export function useUserEuds(id?: string) {
    const { userId, isSupport } = useUserId();
    if (isSupport && !id) return { euds: [] };
    const { data: euds } = useSuspenseQuery({
        ...userQueryOptions({ userId: id ?? userId, isSupport }),
        select: (data) => data?.user.euds ?? [],
    });
    return { euds };
}

export function userQueryOptions({ userId, isSupport }: { userId: string; isSupport?: boolean }) {
    return queryOptions({
        queryKey: ["user", "id", userId],
        queryFn: async () => {
            if (isSupport) return null;
            return httpPostGraphql(queries.user, { id: userId });
        },
    });
}

const queries = {
    user: graphql(`
        query User($id: UUID!) {
            user(userId: $id) {
                id
                name
                language
                status
                statusSince
                topics {
                    id
                    name
                }
                groups {
                    ...GroupLike
                }
                topics {
                    ...TopicLike
                }
                clientPermissions {
                    ...PermissionLike
                }
                euds {
                    ...EudLike
                }
                addresses {
                    name
                    city
                    id
                    state
                    street
                    street2
                    zip
                }
                loginEmail
            }
        }
    `),
    groupWithChildren: graphql(`
        query GroupWithChildren($id: UUID!) {
            group(id: $id) {
                ...GroupLike
                childrenPaged(offset: 0, limit: 100) {
                    ...GroupLike
                }
            }
        }
    `),
};
